import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import {
  AutocompleteCategory,
  LodgingSelectionEnum,
  PlaceIdEnum,
} from "@b2bportal/lodging-api";

import {
  fetchLocations,
  type HotelLocationSuggestion,
  useHotelLocationAutocomplete,
} from "@tenant/hotels";
import { Icon, type LocationAutocompleteProps } from "@tenant/ui";
import defaultStyles from "./HotelLocationAutocomplete.module.scss";
import { Autocomplete } from "@/components";

export interface HotelLocationAutocompleteProps
  extends Omit<
    LocationAutocompleteProps<HotelLocationSuggestion>,
    "fetch" | "groupBy" | "getOptionLabel" | "sortOptions"
  > {
  value?: HotelLocationSuggestion;
  defaultValue?: HotelLocationSuggestion;
}

const optionToIcon = {
  [AutocompleteCategory.airports]: IconNameEnum.airplane,
  [AutocompleteCategory.cities]: IconNameEnum.mapPin,
  [AutocompleteCategory.places]: IconNameEnum.mapPin,
  [AutocompleteCategory.nearby]: IconNameEnum.hotel,
  [AutocompleteCategory.hotelMarkets]: IconNameEnum.hotel,
};

const shouldFetch = (search: string) => search.length >= 2;

export const HotelLocationAutocomplete = (
  props: HotelLocationAutocompleteProps
) => {
  const COMPONENT_KEY = CoreHotelsComponent.HotelLocationAutocomplete;
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { context, handlers } = useHotelLocationAutocomplete(props);

  const getOptionIcon = (option: HotelLocationSuggestion) => {
    // So far the autocomplete only return places, not airports or hotels
    const iconName = option.category
      ? optionToIcon[option.category]
      : IconNameEnum.mapPin;
    return (
      <Icon
        iconName={iconName}
        width="16"
        height="16"
        className={cn("option-icon")}
      />
    );
  };
  const getOptionComponent = (option: HotelLocationSuggestion) => {
    const labelParts = option.label.split(",");
    const labelTitle = labelParts[0].trim();
    const labelSubtitle = labelParts.slice(1).join(", ").trim();
    const icon = getOptionIcon(option);
    return (
      <div className={cn("option-container")}>
        <div className={cn("option-icon-container")}>{icon}</div>
        <div className={cn("option-label-container")}>
          <div className={cn("option-label")}>{labelTitle}</div>
          {labelSubtitle && (
            <div className={cn("option-subtitle")}>{labelSubtitle}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Autocomplete<HotelLocationSuggestion>
      id={context.id}
      defaultValue={context.defaultValue}
      onChange={handlers.onChange}
      fetch={(apiConfig, search, onSuccess) =>
        fetchLocations(search, onSuccess)
      }
      shouldFetch={shouldFetch}
      getOptionLabel={getOptionLabel}
      label={"Where are you staying?"}
      className={block}
      icon={<Icon iconName={IconNameEnum.mapPin} className={cn("icon")} />}
      getOptionComponent={getOptionComponent}
      popperClassName={cn("popper")}
    />
  );
};

const getOptionLabel = (value?: HotelLocationSuggestion) => {
  if (
    value?.id.Id === PlaceIdEnum.Lodgings &&
    value.id.lodgingSelection.LodgingSelection === LodgingSelectionEnum.Place
  ) {
    return value.id.lodgingSelection.searchTerm;
  }
  return "";
};
